import React, {useEffect, useState, useRef} from 'react';
import classnames from 'classnames';
import {connect} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {withRouter} from 'react-router-dom';

import SidebarHeader from './SidebarHeader';
import { SidebarLinks, SidebarSection } from './SidebarLinks';
import SidebarFooter from './SidebarFooter';
import { route, routeTitles } from '../../config/constants';
import './styles.css';
import theme from './theme';
import { isLoggedOut } from '../../helpers/accountHelper';
import { getInspectAccess, INSPECT_ACCESS } from '../../helpers/inspectAccess';

var MIN_SHIMMER_MS = 500;

var SidebarNavShimmer = function () {
  var rows = [
    { titleW: 62, subtitleW: 90 },
    { titleW: 48, subtitleW: 110 },
    { titleW: 70, subtitleW: 80 },
    { titleW: 82, subtitleW: 95 },
    { titleW: 55, subtitleW: 70 },
  ];
  return (
    <div className="sidebar-nav sidebar-nav-shimmer" aria-hidden="true">
      {rows.map(function (row, i) {
        return (
          <div key={i} className="sidebar-shimmer-row">
            <div className="sidebar-shimmer-icon" />
            <div className="sidebar-shimmer-text">
              <div className="sidebar-shimmer-title" style={{ width: row.titleW }} />
              <div className="sidebar-shimmer-subtitle" style={{ width: row.subtitleW }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

var SidebarSlateMateLogo = function() {
  return (
    <div className="sidebar-nav-title sidebar-slatemate-logo">
      <img className="slatemate-nav-normal" src="/images/SlateMate-Logo-normal.svg" alt="SlateMate" height="17" />
      <img className="slatemate-nav-active" src="/images/SlateMate-Logo-active.svg" alt="SlateMate" height="17" />
    </div>
  );
};

const Sidebar = ({
  dashboardSettings,
  app,
  currentSlateViewUUID,
  onClose,
  isHovered,
  isCollapsed,
  onMouseEnter,
  onMouseLeave,
  onToggle,
  location,
  currentAccount,
  allAccountsList,
  currentUser,
  inspectStatus,
}) => {
  const { t } = useTranslation();

  // Show shimmer until data is ready AND minimum display time has elapsed
  var dataReady = !!(allAccountsList && currentAccount);
  var [minTimerDone, setMinTimerDone] = useState(false);
  var showNav = dataReady && minTimerDone;

  useEffect(function () {
    var timer = setTimeout(function () {
      setMinTimerDone(true);
    }, MIN_SHIMMER_MS);
    return function () { clearTimeout(timer); };
  }, []);

  var newBadge = null;
  if (inspectStatus === INSPECT_ACCESS.OFF) {
    newBadge = dashboardSettings && dashboardSettings.inspectLandingSeen === true ? 'dot' : 'new';
  }

  const links = [
    {
      id: 0,
      url: '/accounts/' + (currentAccount ? currentAccount.uuid : '') + '/views/' + currentSlateViewUUID,
      path: route.SLATE_VIEW,
      title: t('sidebar:viewTitle'),
      subtitle: t('sidebar:viewSubtitle'),
      icon: 'data_table',
    },
    {
      id: 4,
      url: '/accounts/' + (currentAccount ? currentAccount.uuid : '') + '/inspect',
      path: route.INSPECT,
      title: t('inspect:sidebarTitle'),
      subtitle: t('inspect:sidebarSubtitle'),
      icon: 'fact_check',
      // NEW while Inspect is off for this user, a dot once the landing page has been seen.
      newBadge: newBadge,
      newBadgeLabel: t('inspectLanding:sidebarNew'),
    },
    {
      id: 2,
      url: route.EXPORT,
      path: route.EXPORT,
      title: t('sidebar:exportTitle'),
      subtitle: t('sidebar:exportSubtitle'),
      icon: 'csv',
    },
  ];

  const slateMateLink = {
    id: 1,
    url: route.SLATE_GENERATOR,
    path: route.SLATE_GENERATOR,
    title: t('sidebar:generateTitle'),
    titleComponent: React.createElement(SidebarSlateMateLogo),
    subtitle: t('sidebar:generateSubtitle'),
    icon: 'auto_awesome',
    badge: 'Preview',
  };

  const linksWithNotifications = [
    ...links,
    {
      id: 3,
      url: route.NOTIFICATIONS,
      path: route.NOTIFICATIONS,
      title: t('sidebar:notifyTitle'),
      subtitle: t('sidebar:notifySubtitle'),
      icon: 'notifications',
    },
    slateMateLink,
  ];

  // Add SlateMate at the end of base links too (when notifications are hidden)
  links.push(slateMateLink);

  const _showScripts = allAccountsList && currentAccount && currentAccount.uuid && allAccountsList.find(item => item.uuid === currentAccount.uuid);
  var hasScriptEditorRole = currentUser && currentUser.roles && currentUser.roles.indexOf('scriptEditor') !== -1;
  const showScripts = (_showScripts && _showScripts.settings && _showScripts.settings.showScripts) || hasScriptEditorRole;

  const adminLinks = [
    showScripts ? {
      id: 99,
      url: route.SCRIPTS,
      path: route.SCRIPTS,
      title: t('sidebar:scriptsTitle'),
      subtitle: t('sidebar:scriptsSubtitle'),
      icon: 'code',
      badge: 'Preview',
    } : null,
    {
      id: 100,
      url: route.STACK,
      path: route.STACK,
      title: t(`routes:${routeTitles.STACK_SETTINGS}`),
      subtitle: currentAccount ? currentAccount.name : '',
      icon: 'settings',
    },
  ].filter(Boolean);

  useEffect(() => {
    onClose();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const _showNotificationTab = allAccountsList && currentAccount && currentAccount.uuid && allAccountsList.find(item => item.uuid === currentAccount.uuid);
  const showNotificationTab = _showNotificationTab && _showNotificationTab.settings;
  const currentLinksList = !!showNotificationTab && showNotificationTab.showNotifications ? linksWithNotifications : links;

  // Build main links (filtered by settings). visibleBySettingKey gates on the
  // user's dashboard settings; visibleByStackSettingKey gates on the current
  // account's stack settings (same source as showNotifications/showScripts).
  const preparedLinks = currentLinksList.filter((link) => {
    if (link.visibleBySettingKey) {
      return !!(dashboardSettings && dashboardSettings[link.visibleBySettingKey]);
    }
    if (link.visibleByStackSettingKey) {
      return !!(showNotificationTab && showNotificationTab[link.visibleByStackSettingKey]);
    }
    return true;
  });

  // Build custom module links by merging user and account modules
  // User modules take precedence when keys match, then sort by key
  const getMergedCustomModules = () => {
    const userModules = currentUser && currentUser.customModules ? currentUser.customModules : [];
    const accountModules = currentAccount && currentAccount.customModules ? currentAccount.customModules : [];

    // Create a map with account modules first, then override with user modules
    const moduleMap = {};
    accountModules.forEach(function(module) {
      if (module.key) {
        moduleMap[module.key] = module;
      }
    });
    userModules.forEach(function(module) {
      if (module.key) {
        moduleMap[module.key] = module;
      }
    });

    // Convert to array and sort by key
    var merged = Object.keys(moduleMap).map(function(key) {
      return moduleMap[key];
    });
    merged.sort(function(a, b) {
      if (a.key < b.key) return -1;
      if (a.key > b.key) return 1;
      return 0;
    });
    return merged;
  };

  const mergedCustomModules = getMergedCustomModules();
  const customLinks = mergedCustomModules.map(function(module) {
    return {
      id: module.key,
      url: '/custom-module/' + module.slug,
      path: route.DYNAMIC_CONTENT,
      title: module.tabName,
      subtitle: module.tabSubtitle,
      icon: module.tabIcon,
    };
  });

  return (
    <nav
      className={classnames('sidebar', {
        hover: isHovered && isCollapsed,
      })}
      style={theme.sidebar}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {!isHovered && isCollapsed && (
        <div className="sidebar-overlay-inner" onClick={onMouseEnter}/>
      )}
      <SidebarHeader
        onClose={onClose}
        onMouseLeave={onMouseLeave}
        isCollapsed={isCollapsed}
        isHovered={isHovered}
      />
      <div className="sidebar-content" style={theme.sidebarContent}>
        <div className="sidebar-nav-wrapper">
          {isLoggedOut(currentUser) ? (
            <div className="sidebar-public-message" style={theme.sidebarContentForLoggedOut}>
              {'This is a public version of this view. ' +
                'For full access, contact the administrator or owner of this Stack.'}
            </div>
          ) : !showNav ? (
            <SidebarNavShimmer />
          ) : (
            <div className="sidebar-nav-fadein">
              <SidebarLinks links={preparedLinks} pathname={location.pathname} />
              {customLinks.length > 0 && (
                <SidebarSection
                  title={t('routes:custom')}
                  shortTitle={t('routes:customShort')}
                  links={customLinks}
                  pathname={location.pathname}
                />
              )}
              <SidebarSection
                title={t('routes:administration')}
                shortTitle={t('routes:administrationShort')}
                links={adminLinks}
                pathname={location.pathname}
              />
            </div>
          )}
        </div>
        <SidebarFooter onToggle={onToggle} isCollapsed={isCollapsed} />
      </div>
    </nav>
  );
};

const mapStateToProps = (state) => ({
  app: state.app,
  currentUser: state.currentUser,
  currentAccount: state.accounts.currentAccount,
  allAccountsList: state.accounts.accountsList,
  dashboardSettings: state.currentUser.dashboardSettings,
  inspectStatus: getInspectAccess(state).status,
  currentSlateViewUUID: state.slateViews.currentSlateView
    ? state.slateViews.currentSlateView.uuid
    : '',
});

export default withRouter(connect(mapStateToProps, null)(Sidebar));
